import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Paper, TablePagination, Select, MenuItem,
  TextField, InputLabel, FormControl, Chip, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import Invoices from '../../collections/invoices';
import InvoiceStatusChip from './InvoiceStatusChip';
import { invoiceStatusTranslations } from '../../utils/invoiceTranslations';

const formatVND = (n) => `${(n || 0).toLocaleString('vi-VN')} đ`;
const sourceLabel = (inv) => {
  if (inv.invoice_type === 'ORDER') return `Đơn hàng #${inv.order_id}`;
  if (inv.invoice_type === 'USER_PACKAGE') return `Gói DV #${inv.user_package_id}`;
  return 'Tùy chỉnh';
};

const STATUS_OPTIONS = ['', 'DRAFT', 'SENT', 'PAID', 'HDDT_CREATED', 'CANCELLED', 'REFUNDED'];
const SEARCH_DEBOUNCE_MS = 400;

const InvoiceList = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [loading, setLoading] = useState(false);

  // Query the API once the user pauses typing, and start again from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = new Invoices();
      const params = {
        ...(status && { status }),
        ...(debouncedQ && { q: debouncedQ }),
      };
      await list.fetch(page + 1, rowsPerPage, params);
      setInvoices(list.toArray());
      setTotal(list.total);
    } catch (e) {
      toast.error('Không thể tải danh sách hóa đơn');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, debouncedQ]);

  useEffect(() => { if (setPageTitle) setPageTitle('Quản lý hóa đơn'); }, [setPageTitle]);
  useEffect(() => { load(); }, [load]);

  return (
    <Container sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                 gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">Quản lý hóa đơn</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate('/invoices/new')}>
          Tạo hóa đơn
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Trạng thái</InputLabel>
          <Select value={status} label="Trạng thái"
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s || 'all'} value={s}>
                {s ? (invoiceStatusTranslations[s] || s) : 'Tất cả'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Tìm kiếm (mã / khách hàng)" value={q}
          onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 280 }} />
        <button onClick={() => { setPage(0); load(); }} style={{ display: 'none' }} />
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2">Tổng quan (trang hiện tại)</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {['DRAFT', 'SENT', 'PAID', 'HDDT_CREATED', 'CANCELLED'].map((s) => {
              const count = invoices.filter((i) => i.status === s).length;
              return <Chip key={s} size="small"
                label={`${invoiceStatusTranslations[s] || s}: ${count}`} />;
            })}
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mã HĐ</TableCell>
              <TableCell>Khách hàng</TableCell>
              <TableCell>Nguồn</TableCell>
              <TableCell align="right">Tổng tiền</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>HDDT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id} hover style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/invoices/${inv.id}`)}>
                <TableCell>{inv.code}</TableCell>
                <TableCell>{inv.user?.name || `#${inv.user_id}`}</TableCell>
                <TableCell>{sourceLabel(inv)}</TableCell>
                <TableCell align="right">{formatVND(inv.total)}</TableCell>
                <TableCell><InvoiceStatusChip status={inv.status} /></TableCell>
                <TableCell>
                  <InvoiceStatusChip variant="hddt"
                    status={inv.government_invoice?.hddt_status} />
                </TableCell>
              </TableRow>
            ))}
            {!invoices.length && (
              <TableRow><TableCell colSpan={6} align="center">
                {loading ? 'Đang tải...' : 'Không có hóa đơn'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>
    </Container>
  );
};

export default InvoiceList;
